import React from 'react';
import type {IProduct} from '../../../../types/galleryTypes';
import {NamePriceLayout} from '../../../../types/galleryTypes';
import a11y from '@wix/wixstores-client-core/dist/es/src/assets/styles/_accessibility.scss';
import s from './ProductPrice.scss';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {withGlobals} from '../../../../globalPropsContext';

import {IGalleryGlobalProps} from '../../../../gallery/galleryGlobalStrategy';
import {
  formattedPrimaryPrice,
  formattedSecondaryPrice,
  hasAutomaticDiscount,
} from '@wix/wixstores-client-core/dist/es/src/productOptions/productUtils';
import {ConditionalRender} from '../../../../category/components/ConditionalRender/ConditionalRender';
import classNames from 'classnames';
import {Experiments} from '../../../../constants';
import {isProductItemHasDiscount, shouldRenderOutOfStock, shouldRenderPrice} from '../productItemUtils';

export enum DataHook {
  SrPriceBeforeDiscount = 'sr-product-item-price-before-discount',
  SrPriceToPay = 'sr-product-item-price-to-pay',
  SrPriceRange = 'st-price-range',
  PriceBeforeDiscount = 'product-item-price-before-discount',
  PriceToPay = 'product-item-price-to-pay',
  PriceRange = 'price-range-from',
  PricesContainer = 'prices-container',
  BasePriceComponent = 'base-price-component',
}

interface TextMapProps {
  productPriceBeforeDiscountSR: string;
  productOutOfStockText: string;
  productPriceAfterDiscountSR: string;
  productPriceWhenThereIsNoDiscountSR: string;
  measurementUnits?: {[key: string]: {[key: string]: string}};
  pricePerUnitSR?: string;
}

export interface IProductPriceProps extends IGalleryGlobalProps {
  product: IProduct;
  allowFreeProducts: boolean;
  textsMap: TextMapProps;
  fromPrice?: string;
  shouldUseCommonDiscountPricingMethods: boolean;
}

const getPriceBeforeDiscount = ({
  textsMap,
  secondaryPrice,
  shouldEnableDiscountAndRegularPriceSwapViewer,
}: {
  textsMap: TextMapProps;
  secondaryPrice: string;
  shouldEnableDiscountAndRegularPriceSwapViewer: boolean;
}) => {
  return (
    <>
      <span className={a11y.srOnly} data-hook={DataHook.SrPriceBeforeDiscount}>
        {textsMap.productPriceBeforeDiscountSR}
      </span>
      <span
        data-hook={DataHook.PriceBeforeDiscount}
        className={classNames({
          [s.priceBeforeDiscount]: shouldEnableDiscountAndRegularPriceSwapViewer,
          [s.oldPriceBeforeDiscount]: !shouldEnableDiscountAndRegularPriceSwapViewer,
        })}
        data-wix-original-price={secondaryPrice}>
        {secondaryPrice}
      </span>
    </>
  );
};

class PriceRangeComp extends React.Component<
  {
    formattedFromPrice: string;
    textsMap: TextMapProps;
  } & IProvidedTranslationProps
> {
  public render() {
    const {formattedFromPrice, textsMap, t} = this.props;
    return (
      <>
        <span className={a11y.srOnly} data-hook={DataHook.SrPriceRange}>
          {textsMap.productPriceWhenThereIsNoDiscountSR}
        </span>
        <span data-hook={DataHook.PriceRange} className={classNames(s.priceFrom)}>
          {t('priceRangeText', {formattedAmount: formattedFromPrice})}
        </span>
      </>
    );
  }
}

const PriceRange = withGlobals(withTranslations()(PriceRangeComp));

const RegularPrice = ({
  product,
  textsMap,
  shouldUseCommonDiscountPricingMethods,
  shouldEnableDiscountAndRegularPriceSwapViewer,
}: {
  product: IProduct;
  textsMap: TextMapProps;
  shouldUseCommonDiscountPricingMethods: boolean;
  shouldEnableDiscountAndRegularPriceSwapViewer: boolean;
}) => {
  const hasDiscount = isProductItemHasDiscount(product, shouldUseCommonDiscountPricingMethods);
  const primaryPrice = getPrimaryPrice({
    product,
    hasDiscount,
    shouldUseCommonDiscountPricingMethods,
  });

  return (
    <>
      {hasDiscount &&
        getSecondaryPrice({
          product,
          textsMap,
          shouldUseCommonDiscountPricingMethods,
          shouldEnableDiscountAndRegularPriceSwapViewer,
        })}
      {
        <>
          <span className={a11y.srOnly} data-hook={DataHook.SrPriceToPay}>
            {hasDiscount ? textsMap.productPriceAfterDiscountSR : textsMap.productPriceWhenThereIsNoDiscountSR}
          </span>
          <span
            data-hook={DataHook.PriceToPay}
            className={classNames({
              [s.salePrice]: hasDiscount && shouldEnableDiscountAndRegularPriceSwapViewer,
              [s.oldSalePrice]: hasDiscount && !shouldEnableDiscountAndRegularPriceSwapViewer,
              [s.priceToPay]: !hasDiscount,
            })}
            data-wix-price={primaryPrice}>
            {primaryPrice}
          </span>
        </>
      }
    </>
  );
};

function getSecondaryPrice({
  product,
  textsMap,
  shouldUseCommonDiscountPricingMethods,
  shouldEnableDiscountAndRegularPriceSwapViewer,
}: {
  product: IProduct;
  textsMap: TextMapProps;
  shouldUseCommonDiscountPricingMethods: boolean;
  shouldEnableDiscountAndRegularPriceSwapViewer: boolean;
}) {
  if (shouldUseCommonDiscountPricingMethods) {
    const selectedSecondaryPrice = formattedSecondaryPrice(product);
    return getPriceBeforeDiscount({
      textsMap,
      secondaryPrice: selectedSecondaryPrice,
      shouldEnableDiscountAndRegularPriceSwapViewer,
    });
  } else {
    const isDiscountsExist =
      Boolean(product.itemDiscount?.priceAfterDiscount) && Boolean(product.formattedComparePrice);
    const selectedSecondaryPrice = isDiscountsExist ? product.formattedComparePrice : product.formattedPrice;

    return getPriceBeforeDiscount({
      textsMap,
      secondaryPrice: selectedSecondaryPrice,
      shouldEnableDiscountAndRegularPriceSwapViewer,
    });
  }
}

function getPrimaryPrice({
  product,
  hasDiscount,
  shouldUseCommonDiscountPricingMethods,
}: {
  product: IProduct;
  hasDiscount: boolean;
  shouldUseCommonDiscountPricingMethods: boolean;
}) {
  if (shouldUseCommonDiscountPricingMethods) {
    const itemDiscount = product.itemDiscount;
    hasAutomaticDiscount(product);
    return formattedPrimaryPrice({...product, itemDiscount});
  } else {
    const discountPrice = product.itemDiscount?.priceAfterDiscount || product.formattedComparePrice;

    return hasDiscount ? discountPrice : product.formattedPrice;
  }
}

class ProductPriceImpl extends React.Component<IProductPriceProps, any> {
  private renderPrice() {
    const {product, textsMap, fromPrice, shouldUseCommonDiscountPricingMethods, globals} = this.props;
    const {styles, stylesParams, useExperiments} = globals;
    const shouldEnableDiscountAndRegularPriceSwapViewer = globals.useExperiments.enabled(
      Experiments.EnableDiscountAndRegularPriceSwapViewer
    );
    const isShowNamePriceSideBySideEnabled = useExperiments.enabled(Experiments.ShouldShowNamePriceSideBySideInViewer);
    const shouldShowNameAbovePrice =
      !isShowNamePriceSideBySideEnabled ||
      styles.get(stylesParams.gallery_namePriceLayout) === NamePriceLayout.NameAbovePrice;
    const shouldShowNamePriceSideBySide =
      isShowNamePriceSideBySideEnabled &&
      styles.get(stylesParams.gallery_namePriceLayout) === NamePriceLayout.NamePriceSideBySide;

    return (
      <div
        className={classNames(s.prices, {
          [s.pricesFlexProperties]: shouldEnableDiscountAndRegularPriceSwapViewer,
          [s.pricesNameAbovePrice]: shouldShowNameAbovePrice,
          [s.pricesNamePriceSideBySide]: shouldShowNamePriceSideBySide,
        })}
        data-hook={DataHook.PricesContainer}>
        {fromPrice ? (
          <PriceRange formattedFromPrice={fromPrice} textsMap={textsMap} />
        ) : (
          <RegularPrice
            shouldUseCommonDiscountPricingMethods={shouldUseCommonDiscountPricingMethods}
            product={product}
            textsMap={textsMap}
            shouldEnableDiscountAndRegularPriceSwapViewer={shouldEnableDiscountAndRegularPriceSwapViewer}
          />
        )}
      </div>
    );
  }

  public render() {
    const {product, allowFreeProducts, shouldUseCommonDiscountPricingMethods} = this.props;
    const shouldRenderPriceLayout = shouldRenderPrice(
      product,
      allowFreeProducts,
      shouldUseCommonDiscountPricingMethods
    );
    const shouldRenderOutOfStockLayout = shouldRenderOutOfStock(product);

    if (shouldRenderOutOfStockLayout) {
      return (
        <div className={s.priceContainer}>
          <ConditionalRender by={'showAddToCartButton'} className={s.outOfStockPriceContainer}>
            {this.renderPrice()}
          </ConditionalRender>
        </div>
      );
    } else if (shouldRenderPriceLayout) {
      return <div className={s.priceContainer}>{this.renderPrice()}</div>;
    }
    return null;
  }
}

export const ProductPrice = withGlobals(ProductPriceImpl);
